/**
 *  @swagger
 *  definitions:
 *    AdvertisementParams:
 *      type: object
 *      properties:
 *        RegionSeasonId:
 *          description: ID of the associated RegionSeason
 *          type: string
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Uid:
 *          description: Uid of the Advertisement
 *          type: string
 *        Public:
 *          description: Public of the Advertisement
 *          type: boolean
 *        Name:
 *          description: Name of the Advertisement
 *          type: string
 *        Type:
 *          description: Type of the Advertisement
 *          $ref: '#/components/enums/AdvertisementType'
 *        Content:
 *          description: Content of the Advertisement
 *          type: string
 *        Email:
 *          description: Email of the Advertisement
 *          type: string
 *        Phone:
 *          description: Phone of the Advertisement
 *          type: string
 *        EndDate:
 *          description: End Date of the Advertisement
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    AdvertisementCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdvertisementParams'
 *        - type: object
 *          required:
 *            - RegionSeasonId
 *            - Uid
 *            - Name
 *            - Type
 *            - Content
 *            - Email
 *
 *  @swagger
 *  definitions:
 *    AdvertisementQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Advertisement
 *              type: string
 *        - $ref: '#/definitions/AdvertisementParams'
 *
 *  @swagger
 *  definitions:
 *    AdvertisementUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdvertisementQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Advertisement:
 *      allOf:
 *        - $ref: '#/definitions/AdvertisementCreateParams'
 *        - $ref: '#/definitions/AdvertisementUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Advertisement:
 *        allOf:
 *          - $ref: '#/definitions/Advertisement'
 *          - type: object
 *            properties:
 *              RegionSeason:
 *                description: RegionSeason of the Advertisement
 *                $ref: '#/components/schemas/RegionSeason'
 *              Regatta:
 *                description: Regatta of the Advertisement
 *                $ref: '#/components/schemas/Regatta'
 *              Crewmember:
 *                description: Crewmember of the Advertisement
 *                $ref: '#/components/schemas/Crewmember'
 */

// Enums
import {
  AdvertisementType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: null | string;
  RegionSeasonId: string;
};

export type $Params = {
  Content: string;
  Email: string;
  EndDate?: Date | null;
  Name: string;
  Phone: null | string;
  Public: boolean;
  Type: AdvertisementType;
  Uid: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Crewmember?: $Model<'Crewmember'>;
  Regatta?: $Model<'Regatta'>;
  RegionSeason?: $Model<'RegionSeason'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: false,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  EndDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Phone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(AdvertisementType)),
    validate: {
      notEmpty: true,
    },
  },
  Uid: {
    allowNull: false,
    type: Sequelize.STRING,
    unique: true,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Advertisement'> => {
  const Advertisement = stack.define(
    'Advertisement',
    fields(Sequelize),
    {
      tableName: 'Advertisements',
    },
  );

  Advertisement.associate = (models: $ModelClasses) => {
    models.Advertisement.belongsTo(
      models.RegionSeason,
      {
        as: 'RegionSeason',
        foreignKey: 'RegionSeasonId',
      },
    );
    models.Advertisement.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        constraints: false,
        foreignKey: 'RegattaId',
      },
    );
    models.Advertisement.hasOne(
      models.Crewmember,
      {
        as: 'Crewmember',
        constraints: false,
        foreignKey: 'Email',
        sourceKey: 'Email',
      },
    );
  };

  Advertisement.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return Advertisement;
});
